/**
 * DatePanel Component
 *
 * The popup panel containing calendar, presets, and action buttons.
 * Compact layout with horizontal presets at top.
 */

import React, { useCallback, useState } from 'react'
import { Calendar, TimePicker, Button, Select } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import type { DatePanelProps } from '../types'

// Month options for dropdown
const MONTHS = [
  { value: 0, label: 'Jan' },
  { value: 1, label: 'Feb' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Apr' },
  { value: 4, label: 'May' },
  { value: 5, label: 'Jun' },
  { value: 6, label: 'Jul' },
  { value: 7, label: 'Aug' },
  { value: 8, label: 'Sep' },
  { value: 9, label: 'Oct' },
  { value: 10, label: 'Nov' },
  { value: 11, label: 'Dec' },
]

// Generate year options (current year - 5 to current year + 10)
const currentYear = dayjs().year()
const YEARS = Array.from({ length: 16 }, (_, i) => ({
  value: currentYear - 5 + i,
  label: String(currentYear - 5 + i),
}))

/**
 * Renders the dropdown panel for date selection.
 *
 * Compact layout:
 * +----------------------------------------+
 * | [Immediately] [Next Sun] [+30d] [+90d] |
 * +----------------------------------------+
 * | <  [Jan v] [2026 v]                  > |
 * | Su Mo Tu We Th Fr Sa                   |
 * | ...calendar...                         |
 * +----------------------------------------+
 * | Time: [ 5:30 PM ]     [Cancel] [Apply] |
 * +----------------------------------------+
 */
export const DatePanel: React.FC<DatePanelProps> = ({
  committedValue,
  pendingValue,
  wantsSemanticNull,
  hasChange,
  semanticConfig,
  presets,
  showTime,
  minDate,
  maxDate,
  disabledDateMessage,
  onSelectDate,
  onSelectSemanticNull,
  onApply,
  onCancel,
}) => {
  // Calendar display month/year (independent of selected date for navigation)
  const [displayDate, setDisplayDate] = useState<Dayjs>(
    pendingValue ?? committedValue ?? dayjs()
  )

  // Check if a date is disabled (outside min/max range)
  const isDateDisabled = useCallback(
    (date: Dayjs): boolean => {
      if (minDate && date.isBefore(minDate, 'day')) {
        return true
      }
      if (maxDate && date.isAfter(maxDate, 'day')) {
        return true
      }
      return false
    },
    [minDate, maxDate]
  )

  // Handle calendar date selection - preserve time if already set, default to midnight
  const handleCalendarSelect = useCallback(
    (date: Dayjs) => {
      // Don't allow selection of disabled dates
      if (isDateDisabled(date)) {
        return
      }

      const existingTime = pendingValue ?? committedValue
      if (showTime) {
        // For datetime fields: preserve existing time or default to midnight
        // Always normalize seconds/milliseconds to zero
        const hour = existingTime ? existingTime.hour() : 0
        const minute = existingTime ? existingTime.minute() : 0
        const withTime = date
          .hour(hour)
          .minute(minute)
          .second(0)
          .millisecond(0)
        onSelectDate(withTime)
      } else {
        // For date-only fields: start of day (already normalized)
        onSelectDate(date.startOf('day'))
      }
      // Also update display date to match selection
      setDisplayDate(date)
    },
    [onSelectDate, pendingValue, committedValue, showTime, isDateDisabled]
  )

  // Handle time selection - preserve the date, normalize seconds to zero
  const handleTimeChange = useCallback(
    (time: Dayjs | null) => {
      if (!time) return
      const currentDate = pendingValue ?? committedValue ?? dayjs()
      // Always normalize seconds/milliseconds to zero for clean timestamps
      const withTime = currentDate
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .millisecond(0)
      onSelectDate(withTime)
    },
    [onSelectDate, pendingValue, committedValue]
  )

  // Handle midnight quick-select - reset time to 00:00:00
  const handleMidnight = useCallback(() => {
    const currentDate = pendingValue ?? committedValue ?? dayjs()
    const atMidnight = currentDate
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
    onSelectDate(atMidnight)
  }, [onSelectDate, pendingValue, committedValue])

  // Navigation handlers
  const handlePrevMonth = useCallback(() => {
    setDisplayDate((d) => d.subtract(1, 'month'))
  }, [])

  const handleNextMonth = useCallback(() => {
    setDisplayDate((d) => d.add(1, 'month'))
  }, [])

  const handleMonthChange = useCallback((month: number) => {
    setDisplayDate((d) => d.month(month))
  }, [])

  const handleYearChange = useCallback((year: number) => {
    setDisplayDate((d) => d.year(year))
  }, [])

  // Custom FULL cell renderer to show selection state and disabled dates
  const fullCellRender = useCallback(
    (current: Dayjs, info: { originNode: React.ReactElement; today: Dayjs; type: string }) => {
      if (info.type !== 'date') {
        return info.originNode
      }

      const isSelected = pendingValue && current.isSame(pendingValue, 'day')
      const isCommitted = !pendingValue && committedValue && current.isSame(committedValue, 'day')
      const isToday = current.isSame(dayjs(), 'day')
      const isDisabled = isDateDisabled(current)

      let className = 'semantic-date-panel__day'
      if (isSelected) className += ' semantic-date-panel__day--selected'
      if (isCommitted) className += ' semantic-date-panel__day--committed'
      if (isToday) className += ' semantic-date-panel__day--today'
      if (isDisabled) className += ' semantic-date-panel__day--disabled'

      // If disabled, show tooltip with message explaining why
      if (isDisabled && disabledDateMessage) {
        return (
          <div className={className} title={disabledDateMessage}>
            {current.date()}
          </div>
        )
      }

      return <div className={className}>{current.date()}</div>
    },
    [pendingValue, committedValue, isDateDisabled, disabledDateMessage]
  )

  // Check if a preset is active
  const isPresetActive = (presetValue: Dayjs) => {
    if (wantsSemanticNull) return false
    const selected = pendingValue ?? committedValue
    return selected && selected.isSame(presetValue, 'day')
  }

  return (
    <div className="semantic-date-panel" role="dialog" aria-label="Date picker">
      {/* Horizontal presets row at top */}
      <div className="semantic-date-panel__presets">
        {semanticConfig && (
          <Button
            size="small"
            type={wantsSemanticNull ? 'primary' : 'text'}
            onClick={onSelectSemanticNull}
            aria-pressed={wantsSemanticNull}
            className="semantic-date-panel__preset-btn"
          >
            {semanticConfig.buttonLabel}
          </Button>
        )}
        {presets !== false &&
          presets.map((preset) => {
            const presetValue = preset.getValue()
            const isActive = isPresetActive(presetValue)
            const presetDisabled = isDateDisabled(presetValue)
            return (
              <Button
                key={preset.key}
                size="small"
                type={isActive ? 'primary' : 'text'}
                disabled={presetDisabled}
                title={presetDisabled ? disabledDateMessage : undefined}
                onClick={() => {
                  if (!presetDisabled) {
                    onSelectDate(presetValue)
                    setDisplayDate(presetValue)
                  }
                }}
                className="semantic-date-panel__preset-btn"
              >
                {preset.label}
              </Button>
            )
          })}
      </div>

      {/* Custom navigation header */}
      <div className="semantic-date-panel__nav">
        <Button
          type="text"
          size="small"
          icon={<LeftOutlined />}
          onClick={handlePrevMonth}
          className="semantic-date-panel__nav-btn"
        />
        <div className="semantic-date-panel__nav-selects">
          <Select
            size="small"
            value={displayDate.month()}
            onChange={handleMonthChange}
            options={MONTHS}
            className="semantic-date-panel__month-select"
            popupMatchSelectWidth={false}
          />
          <Select
            size="small"
            value={displayDate.year()}
            onChange={handleYearChange}
            options={YEARS}
            className="semantic-date-panel__year-select"
            popupMatchSelectWidth={false}
          />
        </div>
        <Button
          type="text"
          size="small"
          icon={<RightOutlined />}
          onClick={handleNextMonth}
          className="semantic-date-panel__nav-btn"
        />
      </div>

      {/* Calendar (using displayDate for panel, calendarValue for selection) */}
      <div className="semantic-date-panel__calendar">
        <Calendar
          fullscreen={false}
          value={displayDate}
          onSelect={handleCalendarSelect}
          fullCellRender={fullCellRender}
          headerRender={() => null}
        />
      </div>

      {/* Action bar with optional time picker */}
      <div className="semantic-date-actions">
        {showTime && (
          <div className="semantic-date-actions__time">
            <span className="semantic-date-actions__time-label">Time:</span>
            <TimePicker
              size="small"
              value={pendingValue ?? committedValue ?? dayjs().startOf('day')}
              onChange={handleTimeChange}
              format="h:mm A"
              use12Hours
              allowClear={false}
              className="semantic-date-actions__time-picker"
              classNames={{ popup: { root: 'semantic-date-actions__time-picker-dropdown' } }}
              renderExtraFooter={() => (
                <div className="semantic-date-actions__time-shortcuts">
                  <Button
                    size="small"
                    type="link"
                    onClick={handleMidnight}
                  >
                    Midnight
                  </Button>
                </div>
              )}
            />
          </div>
        )}
        <div className="semantic-date-actions__buttons">
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={!hasChange}
            onClick={onApply}
            className={hasChange ? 'semantic-date-actions__apply--has-changes' : ''}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}
